import type { FieldManifest, FieldPack } from '../../../../core/fields/types';
import { FieldType } from '../../../../core/fields/types';

export const orgchartTalentFieldPack: FieldPack = {
  id: 'orgchart-talent',
  name: 'Talent Context',
  description: 'Source-owned, restricted talent observations hydrated for permissioned planning context',
  version: '1.0.0',
  dependencies: ['orgchart-base'],
  fields: ([
    {
      id: 'performance',
      label: 'Performance Rating',
      type: FieldType.SELECT,
      tags: ['talent', 'performance'],
      priority: 90,
      namespace: 'talent',
      options: [
        { value: 'exceptional', label: 'Exceptional' },
        { value: 'exceeds', label: 'Exceeds Expectations' },
        { value: 'meets', label: 'Meets Expectations' },
        { value: 'developing', label: 'Developing' },
        { value: 'below', label: 'Below Expectations' },
      ],
      sensitivity: 'manager',
    },
    {
      id: 'potential',
      label: 'Potential Rating',
      type: FieldType.SELECT,
      tags: ['talent', 'potential'],
      priority: 85,
      namespace: 'talent',
      options: [
        { value: 'high', label: 'High Potential' },
        { value: 'medium', label: 'Medium Potential' },
        { value: 'low', label: 'Developing Potential' },
      ],
      sensitivity: 'manager',
    },
    {
      id: 'nineBoxPlacement',
      label: '9-Box Placement',
      type: FieldType.SELECT,
      tags: ['talent'],
      priority: 80,
      namespace: 'talent',
      options: [
        { value: 'star', label: 'Star (High/High)' },
        { value: 'high_performer', label: 'High Performer (High/Med)' },
        { value: 'consistent_star', label: 'Consistent Star (Med/High)' },
        { value: 'key_contributor', label: 'Key Contributor (Med/Med)' },
        { value: 'solid_performer', label: 'Solid Performer (High/Low)' },
        { value: 'growth_employee', label: 'Growth Employee (Low/High)' },
        { value: 'effective', label: 'Effective (Med/Low)' },
        { value: 'inconsistent', label: 'Inconsistent (Low/Med)' },
        { value: 'risk', label: 'Risk (Low/Low)' },
      ],
      sensitivity: 'hr_restricted',
    },
    {
      id: 'readiness',
      label: 'Succession Readiness',
      type: FieldType.SELECT,
      tags: ['talent', 'succession'],
      priority: 75,
      namespace: 'talent',
      options: [
        { value: 'ready_now', label: 'Ready Now' },
        { value: 'ready_1_2', label: 'Ready in 1-2 Years' },
        { value: 'ready_3_5', label: 'Ready in 3-5 Years' },
        { value: 'not_ready', label: 'Not Ready' },
      ],
      sensitivity: 'manager',
    },
    {
      id: 'flightRisk',
      label: 'Flight Risk',
      type: FieldType.SELECT,
      tags: ['talent', 'retention'],
      priority: 70,
      namespace: 'talent',
      options: [
        { value: 'high', label: 'High' },
        { value: 'medium', label: 'Medium' },
        { value: 'low', label: 'Low' },
      ],
      sensitivity: 'manager',
    },
    {
      id: 'impactOfLoss',
      label: 'Impact of Loss',
      type: FieldType.SELECT,
      tags: ['talent', 'retention'],
      priority: 65,
      namespace: 'talent',
      options: [
        { value: 'critical', label: 'Critical' },
        { value: 'high', label: 'High' },
        { value: 'medium', label: 'Medium' },
        { value: 'low', label: 'Low' },
      ],
      sensitivity: 'manager',
    },
    {
      id: 'futureLeader',
      label: 'Future Leader',
      type: FieldType.BOOLEAN,
      tags: ['talent'],
      priority: 60,
      namespace: 'talent',
      sensitivity: 'manager',
    },
    {
      id: 'talentPool',
      label: 'Talent Pool',
      type: FieldType.SELECT,
      allowCustomOptions: true,
      tags: ['talent'],
      priority: 55,
      namespace: 'talent',
      sensitivity: 'manager',
    },
    {
      id: 'calibrationRating',
      label: 'Calibration Rating',
      type: FieldType.SELECT,
      allowCustomOptions: true,
      tags: ['talent', 'performance'],
      priority: 50,
      namespace: 'talent',
      sensitivity: 'manager',
    },
    {
      id: 'lastReviewDate',
      label: 'Last Review Date',
      type: FieldType.DATE,
      tags: ['talent', 'performance'],
      priority: 45,
      namespace: 'talent',
      sensitivity: 'manager',
    },
    {
      id: 'nextReviewDate',
      label: 'Next Review Date',
      type: FieldType.DATE,
      tags: ['talent', 'performance'],
      priority: 40,
      namespace: 'talent',
      sensitivity: 'manager',
    },
    {
      id: 'overallRating',
      label: 'Overall Review Score',
      type: FieldType.NUMBER,
      tags: ['talent', 'performance'],
      priority: 35,
      namespace: 'talent',
      min: 1,
      max: 5,
      step: 0.1,
      sensitivity: 'manager',
    },
    {
      id: 'engagementScore',
      label: 'Engagement Score',
      type: FieldType.NUMBER,
      tags: ['talent', 'engagement'],
      priority: 30,
      namespace: 'talent',
      min: 0,
      max: 100,
      sensitivity: 'manager',
    },
  ] satisfies FieldManifest[]).map((field) => ({
    ...field,
    sensitivity: 'hr_restricted' as const,
    readonly: true,
    sourceTypes: ['talent_observation'],
    metadata: {
      projectionOwned: true,
      legacyScalar: true,
    },
  })),
};
